/*!
  Pioneer WFRIA FileInput v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["FileInput"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("FileInput") < 0) {
    window.__PIONEER_GLOBAL__.components.push("FileInput");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["accept", "customValidators", "customStrings", "errorMessageCallback", "label", "labelHidden", "labelColSize", "fieldColSize", "required", "disabled", "maxSize", "name", "noAsterisk", "onValueChange", "onProgressCancel", "onRemove", "progress", "id", "value", "trigger", "showCancelProgressControl"],
      _excluded2 = ["percentage", "status", "aria-valuetext"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import isAcceptable from "attr-accept";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { DESCRIBEDBY_TIMEOUT } from "../common/constants";
import { useFocusFieldOnError, useUniqueId } from "../common/hooks";
import { invalidIf } from "../common/proptypes";
import { getDataTransferItems, registerField } from "../common/utilities";
import { FormContext } from "../Form/FormContext";
import BaseButton from "../private/BaseButton/BaseButton";
import FieldFooter from "../private/FieldFooter/FieldFooter";
import Tooltip from "../private/Tooltip/Tooltip";
import { BUTTON, CANCEL, CANCEL_BUTTON, REMOVE_BUTTON } from "./constants";
import extractTheme from "./extractTheme";
import ProgressBar from "./ProgressBar";
import STRINGS from "./strings";
import { Flexgrid, FlexgridItem, IconAdd, IconClear, IconConfirmation, IconRequired } from "..";
/**
 * based on the `required` prop and theme
 * renderRequiredIcon decides if the required icon to be
 * displayed or not.
 *
 * As the complexity of the `Fileinput` has reached maximum,
 * so, this function is created outside of the component function.
 * @param {Boolean} required
 * @param {Boolean} noAsterisk
 * @param {Object} cssRoot
 * @returns Boolean whether to display the required icon or not.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function renderRequiredIcon(required, noAsterisk, showRequiredIcon) {
  return required && !noAsterisk && showRequiredIcon;
}
/**
 * FileInput is a form element designed to support single file uploads.
 * Integration with a backend is required for upload and not provided with the component.
 */


const FileInput = /*#__PURE__*/React.forwardRef(
/* eslint-disable-next-line complexity */
(_ref, userRef) => {
  var _trigger$props;

  let {
    accept,
    customValidators,
    customStrings,
    errorMessageCallback,
    label,
    labelHidden,
    labelColSize,
    fieldColSize,
    required,
    disabled,
    maxSize,
    name,
    noAsterisk,
    onValueChange,
    onProgressCancel,
    onRemove,
    progress,
    id,
    value,
    trigger,
    showCancelProgressControl
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    register,
    unregister,
    resetDone,
    isSubmitting
  } = useContext(FormContext) || {};
  const {
    cssRoot,
    cssConfirmationWrapper,
    cssLabelContainer,
    cssTriggerContainer,
    cssTriggerContentBox,
    cssTriggerButton,
    cssFileDetails,
    cssClearButton,
    cssProgressBarWrapper,
    cssProgressBarContentBox,
    cssFileSuccessWrapper,
    cssFooterError
  } = extractTheme(_objectSpread({
    theme
  }, otherProps));
  const fileInputTriggerId = useUniqueId({
    id: trigger === null || trigger === void 0 ? void 0 : (_trigger$props = trigger.props) === null || _trigger$props === void 0 ? void 0 : _trigger$props.id
  });
  const removeFileButtonAriaLabelledby = `pnr-file-input__removeFileButtonAriaLabelledby-${fileInputTriggerId}`;
  const errorId = `pnr-file-input-error-${fileInputTriggerId}`;
  const hiddenInputId = `pnr-file-input__input-${fileInputTriggerId}`;
  const fileDetailsId = `pnr-file-input__fileDetails${fileInputTriggerId}`;
  const hiddenInput = id ? id : hiddenInputId;
  const hiddenInputRef = useRef();
  const dropzoneRef = useRef();
  const changeEventRef = useRef();
  let triggerConfiguration = null;
  useFocusFieldOnError(hiddenInputRef, dropzoneRef, isSubmitting);

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    requiredErrorMessage
  } = componentStrings;
  const requiredMessage = typeof requiredErrorMessage === "string" ? requiredErrorMessage : requiredErrorMessage(label);
  /**
   * Errors should be first shown on blur, and then on change.
   * This state "displayErrors" is used to that effect: even
   * if the Form is reporting errors, we do not show them
   * before onBlur is triggered and change this state.
   *
   * This state is also reverted back to false on reset.
   */

  const [displayErrors, setDisplayErrors] = useState(false);
  const [errorIcon, setErrorIcon] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadedFile, setUploadedFile] = useState(value || null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showFileProgressSuccess, setShowFileProgressSuccess] = useState(false);
  const [triggerAriaDescribedBy, setTriggerAriaDescribedBy] = useState("");
  const status = progress === null || progress === void 0 ? void 0 : progress.status;
  /**
   * trigger the click on native input element.
   */

  const openDialog = () => {
    hiddenInputRef.current.value = null;
    hiddenInputRef.current.click();
  };
  /**
   * Safari will not focus the dropzone button on mouse down.
   * handleDropzoneBlur will be called after that and will produce an
   * error. preventDefault will prevent this blur event from firing.
   */


  const handleDropzoneMouseDown = evt => {
    evt.preventDefault();
  };
  /**
   *
   * @param {Event} event object.
   */


  const handleDropzoneClick = event => {
    event.currentTarget.focus();
    event.stopPropagation();
    openDialog();
    const triggerClick = trigger && trigger.props.onClick;
    triggerClick && triggerClick(event);
  };
  /**
   * Fired when an element is being dragged over the
   * drop target (every few hundred milliseconds).
   * @param {Event} event object.
   */


  const handleDropzoneDragOver = event => {
    event.preventDefault();
    event.stopPropagation();
    const triggerDragOver = trigger && trigger.props.onDragOver;
    triggerDragOver && triggerDragOver(event);

    try {
      // Force the cursor to show the (+) sign.
      event.dataTransfer.dropEffect = "copy";
    } catch (err) {// Nothing to declare.
    }

    setIsDragActive(true);
  };
  /**
   * Fired when a dragged element leaves the drop target.
   * @param {Event} event object.
   */


  const handleDropzoneDragLeave = event => {
    event.preventDefault(); // To reset `isDragActive` once the dropzone have been left.

    setIsDragActive(false);
  };
  /**
   * Runs the custom validators passed to the FileInput during the
   * time of validation. The validators are run one after another.
   * If one of the validators returns an error message then
   * rest of the validators are ignored.
   * @param {Object} file uploaded file object.
   * @return {String} customErrorMessage Errors returned by the validators function.
   */


  const runCustomValidators = useCallback(file => {
    if (!customValidators) {
      return;
    }

    const customValidatorsLength = customValidators.length;
    let i = 0,
        customErrorMessage;
    /**
     * Since we cannot break out of a map function,
     * it is better to use a for loop here
     */

    for (i; i < customValidatorsLength; i++) {
      customErrorMessage = customValidators[i](file);

      if (customErrorMessage) {
        break;
      }
    }

    return customErrorMessage;
  }, [customValidators]);
  /**
   * Updates aria-describedby of the trigger to have the id of the error when there is an error.
   */

  const updateAriaDescribedBy = useCallback(() => {
    let ariaDescribedByTimeout = 0;
    setTriggerAriaDescribedBy("");
    clearTimeout(ariaDescribedByTimeout);
    const ariaDescribedBy = `${errorId} ${fileDetailsId}`;
    ariaDescribedByTimeout = setTimeout(() => {
      setTriggerAriaDescribedBy(ariaDescribedBy);
    }, DESCRIBEDBY_TIMEOUT);
  }, [errorId, fileDetailsId]);
  /**
   * This method allows a user to invoke validation upon the FileInput component and
   * also to clear any error validation message based on whether the string `reset` is
   * passed as a parameter or not.
   */

  const validate = useCallback(() => {
    // eslint-disable-next-line no-use-before-define
    const {
      id
    } = triggerConfiguration;

    if (uploadedFile) {
      let rejectionReason;
      const {
        fileFormatErrorMessage,
        fileSizeErrorMessage
      } = STRINGS;
      const {
        name,
        size,
        type
      } = uploadedFile;

      if (!isAcceptable({
        name,
        type
      }, accept)) {
        rejectionReason = fileFormatErrorMessage(uploadedFile);
      } else if (size > maxSize) {
        rejectionReason = fileSizeErrorMessage(uploadedFile);
      }

      if (typeof rejectionReason === "undefined") {
        const customErrorMessage = runCustomValidators(uploadedFile);
        errorMessageCallback && errorMessageCallback(id, customErrorMessage);
        hiddenInputRef.current.setCustomValidity(customErrorMessage ? customErrorMessage : "");
        setError(Boolean(customErrorMessage));
        setErrorMessage(customErrorMessage || null);
        setDisplayErrors(true);

        if (customErrorMessage) {
          updateAriaDescribedBy();
        } else {
          setTriggerAriaDescribedBy(fileDetailsId);
        }

        return customErrorMessage;
      } else {
        hiddenInputRef.current.setCustomValidity(rejectionReason);
        errorMessageCallback && errorMessageCallback(id, rejectionReason);
        setError(true);
        setDisplayErrors(true);
        setErrorMessage(rejectionReason);
        updateAriaDescribedBy();
        return rejectionReason;
      }
    }
    /* istanbul ignore else */


    if (required && !uploadedFile) {
      errorMessageCallback && errorMessageCallback(id, requiredMessage);
      hiddenInputRef.current.setCustomValidity(requiredMessage);
      setError(required);
      setErrorMessage(requiredMessage);
      setErrorIcon(false);
      updateAriaDescribedBy();
    }
  }, [accept, errorMessageCallback, fileDetailsId, maxSize, required, requiredMessage, runCustomValidators, triggerConfiguration, updateAriaDescribedBy, uploadedFile]);
  /**
   * Fired onBlur of drop zone without making selection.
   * @param {Event} event object.
   */

  const handleDropzoneBlur = useCallback(event => {
    /**
     *  Click on dropzone also triggers blur while opening dialogue window.
     *  Restricting validation on dropzone click.
     */
    const triggerBlur = trigger && trigger.props.onBlur;
    triggerBlur && triggerBlur(event);

    if (register) {
      !displayErrors && setDisplayErrors(true);
    }

    if (required && !uploadedFile && !(dropzoneRef.current === document.activeElement)) {
      validate();
    }
  },
  /**
   * As validate method is called at multiple places, it cannot be
   * declared inside the `useCallback` hook, hence disabling the dependencies.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [uploadedFile, required]);
  /**
   * Fired when input file element changes
   * (dropped element(s), open/close OS file selector, etc.).
   * @param {Event} event object
   */

  const handleDropzoneChange = event => {
    // Stop default browser behavior.
    event.preventDefault();
    const [file] = getDataTransferItems(event);
    const triggerDrop = trigger && trigger.props.onDrop;
    triggerDrop && triggerDrop(event); // Getting rerendered in IE, even when we don't have a file selected.

    if (file) {
      setUploadedFile(file);
      setIsDragActive(false);
    }

    changeEventRef.current = event;
  };

  triggerConfiguration = {
    onMouseDown: handleDropzoneMouseDown,
    onClick: handleDropzoneClick,
    onDragOver: handleDropzoneDragOver,
    onDragLeave: handleDropzoneDragLeave,
    onDrop: handleDropzoneChange,
    onBlur: handleDropzoneBlur,
    "aria-describedby": fileDetailsId,
    id: fileInputTriggerId,
    "aria-required": required || null,
    className: clsx({
      "pnr-file-input--active": isDragActive
    }),
    // trigger button ref.
    ref: dropzoneRef,
    noBox: true,
    buttonStyles: _objectSpread({}, cssTriggerButton)
  };
  /**
   *
   * @param {Event} event object
   */

  const handleRemove = e => {
    /**
     * Focus the trigger zone after file is removed.
     */
    setDisplayErrors(false);
    setError(false);
    setUploadedFile(null);
    setTriggerAriaDescribedBy(""); // dropzoneRef is the trigger button.

    dropzoneRef.current.focus();
    hiddenInputRef.current.value = "";
    onRemove && onRemove(e, uploadedFile);
  };
  /**
   * Method to provide file progress status message.
   * @return {String} file status message content to be displayed or to announce.
   */


  const getFileInputProgressStatusMessage = status => {
    const {
      progressStartMessage,
      progressSucceededMessage,
      progressFailedMessage,
      progressCancelledMessage
    } = componentStrings;
    let customMessage = null;
    /**
     * We need to remove the empty default case in order to get 100% coverage.
     */
    // eslint-disable-next-line default-case

    switch (status) {
      case FileInput.PROGRESS_INIT:
        customMessage = progressStartMessage(uploadedFile);
        break;

      case FileInput.PROGRESS_FAILURE:
        customMessage = progressFailedMessage(uploadedFile);
        break;

      case FileInput.PROGRESS_SUCCESS:
        customMessage = progressSucceededMessage(uploadedFile);
        break;

      case CANCEL:
        customMessage = progressCancelledMessage(uploadedFile);
        break;
    }

    return customMessage;
  };
  /**
   * handleCancel triggers on click of 'Cancel' button to stop the file Progress.
   */


  const handleCancel = e => {
    setError(required);
    setErrorIcon(false);
    setErrorMessage(getFileInputProgressStatusMessage(CANCEL)); // Remove the currently loaded file.

    setUploadedFile(null);
    setDisplayErrors(true);
    /**
     * Callback to execute when the file Progress is cancelled.
     */

    onProgressCancel && onProgressCancel(e, uploadedFile);
  };
  /**
   * Method useful for generating the button triggers of Remove and Cancel.
   * @param {String} control Decides the functionality of the button.
   */


  const renderFileHandlerButton = control => {
    const {
      removeButtonLabel,
      cancelButtonLabel,
      selectionStatus,
      selectedText
    } = componentStrings;
    const handler = control === CANCEL_BUTTON ? handleCancel : handleRemove;
    /**
     * Check for the existence of application added `customStrings`. If
     * it exists, and if the application has added a custom string for
     * `removeButtonLabel`, then store the existence as a Boolean.
     */

    const customStringRemoveButtonLabelExists = customStrings ? customStrings.hasOwnProperty(["removeButtonLabel"]) : false;
    /**
     * When applications add their own `customStrings` values, be sure to allow access
     * to the `file` parameter by passing the parameter through to the `customStrings`
     * template `removeButtonLabel`.
     *
     * `removeFileButtonLabelString`:
     * When an application does not provide a `customString` for `removeButtonLabel`,
     * the `file` parameter must not be passed, as the specs indicate that the
     * default language for the Tooltip should show as "Remove file", and not
     * append the name of the file.
     * Default: "Remove file"
     *
     * `removeFileButtonLabelA11yString`:
     * This is the complete string read by screen readers.
     * Default: "Remove file, [file.name]"
     */

    let removeFileButtonLabelA11yString, toolTipLabel;

    if (uploadedFile && control === CANCEL_BUTTON) {
      removeFileButtonLabelA11yString = cancelButtonLabel(uploadedFile);
      toolTipLabel = cancelButtonLabel(uploadedFile);
    } else {
      const removeFileButtonLabelString = customStringRemoveButtonLabelExists ? removeButtonLabel(uploadedFile) : removeButtonLabel();
      removeFileButtonLabelA11yString = removeButtonLabel(uploadedFile);
      toolTipLabel = removeFileButtonLabelString;
    }

    return _jsxs("span", {
      className: "pnr-file-input__fileDetails",
      css: cssFileDetails,
      children: [_jsxs("span", {
        id: fileDetailsId,
        children: [control === REMOVE_BUTTON && selectionStatus(uploadedFile), uploadedFile && _jsx("span", {
          className: "wf-u-visually-hidden",
          children: selectedText
        })]
      }), uploadedFile && _jsxs(React.Fragment, {
        children: [_jsx(Tooltip, {
          autoPlacement: true,
          label: toolTipLabel,
          trigger: _jsx(BaseButton, {
            "aria-labelledby": removeFileButtonAriaLabelledby,
            kind: "simple",
            className: "pnr-file-input__clear",
            centerIcon: _jsx(IconClear, {
              size: "xsmall"
            }),
            onClick: handler,
            buttonStyles: cssClearButton
          })
        }), _jsx("span", {
          className: "wf-u-visually-hidden",
          id: removeFileButtonAriaLabelledby,
          children: removeFileButtonLabelA11yString
        })]
      })]
    });
  };
  /**
   * This method is responsible to render the File upload success acknowledgement message.
   */


  const renderFileSuccessContent = () => {
    return _jsx(Flexgrid, {
      css: cssFileSuccessWrapper,
      children: _jsxs(FlexgridItem, {
        css: cssConfirmationWrapper,
        children: [_jsx("span", {
          children: _jsx(IconConfirmation, {
            className: "pnr-file-input__confirmation-icon",
            size: "small"
          })
        }), componentStrings.selectionStatus(uploadedFile)]
      })
    });
  };
  /**
   * Callback function that is executed after the announcement of completionAnnouncement prop.
   */


  const hideProgressBar = () => {
    setShowFileProgressSuccess(true);
  };
  /**
   * Method responsible for generating HTML wrapper of ProgressBar and Progress cancel button.
   * Utilises the fieldClassName to maintain composition and styling.
   */


  const renderProgressBar = () => {
    const {
      percentage,
      status,
      "aria-valuetext": ariaValueText
    } = progress,
          rest = _objectWithoutProperties(progress, _excluded2);

    return _jsx("div", {
      className: "pnr-file-input__progressBar-container",
      css: cssProgressBarWrapper,
      children: _jsxs(Flexgrid, {
        alignVertical: "flex-start",
        columnGap: 0,
        children: [_jsxs(FlexgridItem, {
          css: cssProgressBarContentBox,
          children: [_jsx(ProgressBar, _objectSpread({
            "aria-valuetext": ariaValueText,
            percentage: percentage,
            initialAnnouncement: getFileInputProgressStatusMessage(FileInput.PROGRESS_INIT),
            completionAnnouncement: status === FileInput.PROGRESS_SUCCESS ? getFileInputProgressStatusMessage(FileInput.PROGRESS_SUCCESS) : "",
            onComplete: hideProgressBar // eslint-disable-next-line jsx-a11y/no-autofocus
            ,
            autoFocus: true
          }, rest)), _jsx("p", {
            "aria-hidden": "true",
            children: ariaValueText
          })]
        }), _jsx(FlexgridItem, {
          children: showCancelProgressControl && renderFileHandlerButton(CANCEL_BUTTON)
        })]
      })
    });
  };

  useEffect(() => {
    if (userRef) {
      userRef.current = {
        /**
         * The value of current is undefined by default
         * hence we cannot leverage object-shorthand
         * approach for adding the public
         * methods, so disabling the lint here.
         *
         */
        // eslint-disable-next-line object-shorthand
        focus: function () {
          dropzoneRef.current.focus();
        }
      };
    }
  }, [dropzoneRef, userRef]);
  useEffect(() => {
    if (uploadedFile !== null) {
      const rejectionReason = validate();
      onValueChange && onValueChange(uploadedFile, changeEventRef.current, rejectionReason);
    }
    /**
     * validate method will be triggered in multiple handlers
     * hence we can define that function inside this `useCallback` hook
     * so disabling the dependencies.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [uploadedFile]);
  useEffect(() => {
    return () => {
      /**
       * Component is being unmounted.
       * Un-registering field with the "unregister" method
       * from react-hook-form.
       */
      unregister && unregister(name);
    };
  }, [name, unregister]);
  useEffect(() => {
    if (value !== null) {
      setUploadedFile(value);
      validate();
    } else if (value === null && value !== uploadedFile) {
      /**
       * This block is for resetting the value to initial
       * state value.
       * Hence resetting the error message as well.
       */
      setUploadedFile(value);
      setError(false);
      setErrorIcon(false);
      setErrorMessage("");
      setDisplayErrors(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [value]);
  /**
   * Below effect should execute only when `resetDone` is true.
   *
   * 1. resetDone will be true, when `reset` button of the form
   * is clicked. As it will reset the value of the native
   * hidden input element, we need to programmmatically
   * sync internal state to empty.
   */

  useEffect(() => {
    if (resetDone) {
      setUploadedFile(null);
      setError(false);
      setErrorIcon(false);
      setErrorMessage("");
      setDisplayErrors(false);
    }
  }, [resetDone]);
  /**
   * Vaidate and show errors if the Form is being submitted even
   * if the field hasn't been blurred yet.
   */

  useEffect(() => {
    if (isSubmitting) {
      validate();
      setDisplayErrors(true);
    }
  }, [isSubmitting, validate]);
  /**
   * Reference STRINGS here, as this represents the
   * default message. Applications that want a
   * different message will add their own custom
   * <Button />, which includes the desired
   * default messages.
   */

  const triggerButtonMessage = uploadedFile ? componentStrings.replaceFileButtonLabel : componentStrings.selectFileButtonLabel;
  /**
   * Return the application provided `trigger` prop
   * ("Select file" button), otherwise, provide a
   * default "Select file" button.
   */

  const getTrigger = () => {
    if (trigger) {
      triggerConfiguration.children = _jsx("span", {
        "aria-hidden": "true",
        children: trigger.props.children
      });
    }

    const customAriaLabel = trigger ? trigger.props.children : triggerButtonMessage;
    const firstTriggerWord = customAriaLabel === null || customAriaLabel === void 0 ? void 0 : customAriaLabel.split(" ")[0];
    const remaningTriggerWords = customAriaLabel === null || customAriaLabel === void 0 ? void 0 : customAriaLabel.replace(firstTriggerWord, "").trim();
    triggerConfiguration.disabled = disabled;
    triggerConfiguration["aria-label"] = `${firstTriggerWord} ${label} ${remaningTriggerWords}`;
    triggerConfiguration["aria-describedby"] = triggerAriaDescribedBy ? triggerAriaDescribedBy : fileDetailsId;
    triggerConfiguration.slim = true;
    const triggerFromProps = trigger ? /*#__PURE__*/React.cloneElement(trigger, triggerConfiguration) : null;
    return triggerFromProps ? triggerFromProps : _jsx(BaseButton, _objectSpread(_objectSpread({
      kind: "standard",
      leftIcon: _jsx(IconAdd, {
        size: "xsmall"
      })
    }, triggerConfiguration), {}, {
      slim: true,
      children: _jsx("span", {
        "aria-hidden": "true",
        children: triggerButtonMessage
      })
    }));
  };
  /**
   * Registering field with the "register" method from
   * react-hook-form.
   */


  const {
    inputRef,
    reactHookFormRest
  } = registerField({
    register,
    name,
    required,
    requiredErrorMessage: requiredMessage,
    customValidators,
    handleChange: handleDropzoneChange
  });
  const labelClassNames = clsx("pnr-file-input__label", {
    "wf-u-visually-hidden": labelHidden
  });
  const progressFailureStatus = (progress === null || progress === void 0 ? void 0 : progress.status) && progress.status === FileInput.PROGRESS_FAILURE;
  return _jsxs(Flexgrid, {
    css: cssRoot,
    direction: cssRoot.flexDirection,
    children: [_jsx(FlexgridItem, {
      span: _objectSpread({}, labelColSize),
      css: cssLabelContainer,
      children: _jsxs("label", {
        htmlFor: fileInputTriggerId,
        className: labelClassNames,
        children: [_jsxs("span", {
          className: "wf-u-visually-hidden",
          children: [" ", trigger ? trigger.props.children : triggerButtonMessage, " "]
        }), renderRequiredIcon(required, noAsterisk, cssRoot === null || cssRoot === void 0 ? void 0 : cssRoot.showRequiredIcon) ? _jsx(IconRequired, {
          display: "inline-block",
          size: "xsmall",
          spacing: {
            r: 0.5
          }
        }) : null, label]
      })
    }), _jsx(FlexgridItem, {
      span: _objectSpread({}, fieldColSize),
      css: cssTriggerContainer,
      children: _jsx("div", {
        children: status && (status === FileInput.PROGRESS_INIT || !progressFailureStatus && !showFileProgressSuccess) ? renderProgressBar() : _jsxs(React.Fragment, {
          children: [_jsxs("div", {
            className: "pnr-file-input__container",
            css: cssTriggerContentBox,
            children: [getTrigger(), _jsx("input", _objectSpread({
              hidden: true,
              id: hiddenInput,
              name: name,
              accept: accept,
              required: required,
              className: "wf-u-visually-hidden",
              type: "file",
              autoComplete: "off",
              ref: e => {
                inputRef && inputRef(e);
                hiddenInputRef.current = e;
              }
              /**
               * This is necessary to pacify Axe.
               * We DO NOT expect it to be announced or accessed,
               * since this input is completely hidden from the UI.
               * https://dequeuniversity.com/rules/axe/3.3/checkboxgroup?application=axeAPI
               */
              ,
              "aria-label": label
            }, reactHookFormRest)), renderFileHandlerButton(REMOVE_BUTTON)]
          }), status === FileInput.PROGRESS_SUCCESS && showFileProgressSuccess && renderFileSuccessContent(), (error || progressFailureStatus) && _jsx(FieldFooter, {
            displayErrors: displayErrors,
            id: errorId,
            label: label,
            noErrorIcon: progressFailureStatus && !errorIcon,
            message: errorMessage || progressFailureStatus && getFileInputProgressStatusMessage(FileInput.PROGRESS_FAILURE),
            footerErrorStyle: cssFooterError
          })]
        })
      })
    })]
  });
});
FileInput.PROGRESS_INIT = "init";
FileInput.PROGRESS_FAILURE = "failure";
FileInput.PROGRESS_SUCCESS = "success";
FileInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Label for the FileInput trigger button. */
  label: PropTypes.node.isRequired,

  /** `name` attribute for the input tag. */
  name: PropTypes.string.isRequired,

  /**
   * Allow specific types of files.
   * See https://github.com/okonet/attr-accept for more information.
   */
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

  /**
   * Override for strings used within the component.
   */
  customStrings: PropTypes.shape({
    /**
     * Label for the Cancel button.
     * @param {Object} File object.
     * Default: '(file) => `Cancel uploading ${file.name}`'.
     */
    cancelButtonLabel: PropTypes.func,

    /**
     * Instructional text, to display when the file format is not accepted.
     * @param {Object} File object.
     * Default text is `File format not accepted`.
     */
    fileFormatErrorMessage: PropTypes.func,

    /**
     * Instructional text, to display when the file size exceeds the maximum.
     * @param {Object} File object.
     * Default text is `File size exceeds maximum`.
     */
    fileSizeErrorMessage: PropTypes.func,

    /**
     * The text to be shown when the file upload progress is cancelled.
     * @param {Object} File object.
     * Default text is `Upload canceled`.
     */
    progressCancelledMessage: PropTypes.func,

    /**
     * The text to be shown when the file upload progress fails.
     * @param {Object} File object.
     * Default text is `Upload failed`.
     */
    progressFailedMessage: PropTypes.func,

    /**
     * The text to be shown when the file upload progress starts.
     * @param {Object} File object.
     * Default text is `Upload started`.
     */
    progressStartMessage: PropTypes.func,

    /**
     * The text to be shown when the file upload progress is successfully completed.
     * @param {Object} File object.
     * Default text is `Upload completed`.
     */
    progressSucceededMessage: PropTypes.func,

    /**
     * Label for the X button ("Remove file"). This button renders after a file
     * has been added to the FileInput.
     * @param {Object} File object.
     * Default: `Remove file, ${file.name}`.
     */
    removeButtonLabel: PropTypes.func,

    /**
     * The error message to be displayed when the FileInput field is required.
     * Default text is `Field is required`.
     */
    requiredErrorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    /**
     * Instructional text to display when the user selects/replaces/hasn't chosen the file.
     * @param {Object} File object.
     * Default text is `name (size)` and `No file selected` with and without File respectively.
     */
    selectionStatus: PropTypes.func,

    /**
     * The hidden text that accompanies the name of the selected file. Therefore, screen readers
     * should read the name of the selected file appended with the "selected" string.
     * For example, "[filename].[filetype] selected."
     * Default text is `selected`.
     */
    selectedText: PropTypes.string
  }),

  /*
   * An array of functions that are executed when value is changed in FileInput.
   * The functions should return a string containing an error message
   * when the test in the validator fails.
   */
  customValidators: PropTypes.arrayOf(PropTypes.func),

  /** Disable or enable the look of FileInput. */
  disabled: PropTypes.bool,

  /**
   * Callback handler to check the validation error of the Fieldset.
   * This prop is received via context API from Form.
   */
  errorMessageCallback: PropTypes.func,

  /** The number of columns required by the container that wraps trigger button and file details. */
  fieldColSize: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** The number of columns required by the container that wraps the label. */
  labelColSize: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** Determines whether to hide the visual label of the FileInput or not. */
  labelHidden: PropTypes.bool,

  /** Maximum size (in bytes) of the file. */
  maxSize: PropTypes.number,

  /**
   * Determines whether to add an asterisk to the left of the label of a required FileInput field.
   */
  noAsterisk: PropTypes.bool,

  /**
   * Callback handler to be executed on click of the cancel button once the progress starts.
   * Required if `showCancelProgressControl` prop is passed.
   * @param {EventFacade} event Click event object.
   * @param {Object} File object Receives an object containing the following signature: {name, type}.
   */
  onProgressCancel: invalidIf(PropTypes.func, (props, propName) => props[propName] !== null && !props.showCancelProgressControl, `when you do not supply "props.showCancelProgressControl".`),

  /**
   * Callback handler to be executed on click of the "Remove" file button.
   * @param {EventFacade} event Click event object.
   * @param {Object} File object Receives an object containing the following signature: {name, type}.
   */
  onRemove: PropTypes.func,

  /**
   * Callback to be executed when value is changed in FileInput.
   * @param {Object} File object Receives an object containing the following signature: {name, type}.
   * @param {EventFacade} e Receives the focus event facade as a parameter.
   * @param {Object} object An object containing the reasons why file was rejected.
   * Each reason will match one of the properties from the FileInput.RejectReason static.
   */
  onValueChange: PropTypes.func,

  /**
   * Use this prop to show the [ProgressBar](#/Components/ProgressBar) component once the
   * file upload progress starts.
   */
  progress: PropTypes.shape({
    /**
     * The text that conveys the current progress information.
     */
    "aria-valuetext": PropTypes.string.isRequired,

    /**
     * The percentage of completion.
     * If it is passed then ProgressBar would behave as determinate,
     * otherwise it would be indeterminate.
     */
    percentage: PropTypes.number,

    /**
     * Determines the status of the file upload progress. Set it to
     * `FileInput.PROGRESS_INIT` to display ProgressBar,
     * `FileInput.PROGRESS_SUCCESS` to display file upload progress succeeded message and
     * `FileInput.PROGRESS.FAILURE` to display file upload progress failure message.
     */
    status: PropTypes.oneOf([FileInput.PROGRESS_INIT, FileInput.PROGRESS_SUCCESS, FileInput.PROGRESS_FAILURE]).isRequired
  }),

  /** This prop is used to determine if FileInput is required. */
  required: PropTypes.bool,

  /**
   * Determines whether the cancel progress button should be displayed or not.
   */
  showCancelProgressControl: PropTypes.bool,

  /**
   * FileInput accepts only [Button](#/Components/Button)
   * as a trigger.
   */
  trigger: invalidIf(PropTypes.node, (props, propName) => props[propName] && props[propName].props.type !== BUTTON, "when `trigger` prop is a node other than Button."),

  /**
   * File object as a value supplied to FileInput.
   * The App will have to set it based on the following events:
   * `onValueChange`, `onProgressCancel` and `onRemove`.
   */
  value: PropTypes.object // eslint-disable-line react/forbid-prop-types

} : {};
FileInput.defaultProps = {
  accept: null,
  customStrings: null,
  customValidators: null,
  errorMessageCallback: null,
  fieldColSize: {
    xs: 12,
    sm: 8
  },
  nativeValidationMessages: null,
  noAsterisk: false,
  onProgressCancel: null,
  onRemove: null,
  progress: null,
  label: null,
  labelHidden: false,
  labelColSize: {
    xs: 12,
    sm: 4
  },
  required: false,
  disabled: false,
  maxSize: Infinity,
  onValueChange: null,
  id: null,
  showCancelProgressControl: false,
  trigger: null,
  value: null
};
FileInput.displayName = "FileInput";
export default FileInput;